import { MailOutlined } from "@ant-design/icons";
import uitoolkit, { CustomizationOptions } from "@zoom/videosdk-ui-toolkit";
import "@zoom/videosdk-ui-toolkit/dist/videosdk-ui-toolkit.css";
import { Button, Drawer, List, Spin, Modal, Tooltip, Typography } from "antd";
import { message } from "antd";
import { Centrifuge, PublicationContext } from "centrifuge";
import { useCallback, useEffect, useRef, useState } from "react";

import { useToken } from "../context/TokenContext";
import {
  checkCallStatus,
  inviteUser,
  OnlineUser,
  pollOnlineUsers
} from "../data/api";
import { callWs } from "../data/ws";

import {
  checkCallStatusTime,
  checkUserAloneInCallTime,
  onlineUserPollingTime
} from "../utils/constants";
import { CallStatusEnum, Role } from "../utils/enums";
// import { getPatientWaitTimeInSeconds } from '../utils/timeUtils';
import "./App.css";
import React from "react";

// interface VideoCallConfig {
//   videoSDKJWT: string;
//   sessionName: string;
//   userName: string;
//   sessionPasscode: string;
//   crDisclaimer: string;
//   lttDisclaimer: string;
//   livestreamDisclaimer: string;
//   featureOptions: object;
//   // features: string[];
//   // options: Record<string, unknown>;
// }

interface Props {
  callToken: string;
  sessionName: string;
  sessionPasscode: string;
  userName: string;
  callId: string;
  userId?: string;
  role: string;
  isPreviewEnabled: boolean;
  patientUUID?: string;
  sessionStartedAt?: string;
  isPipModeEnabled?: boolean;
}

const VideoCall: React.FC<Props> = ({
  sessionName,
  sessionPasscode,
  callToken,
  userName,
  callId,
  userId,
  role,
  isPreviewEnabled = false,
  patientUUID,
  sessionStartedAt,
  isPipModeEnabled
}) => {
  const sessionContainerRef = useRef<HTMLDivElement | null>(null);
  const [showSideMenu, setShowSideMenu] = useState(false);
  const [doctors, setDoctors] = useState<OnlineUser[] | undefined>();
  const [isCallJoined, setIsCallJoined] = useState(false);
  const [invitingUserId, setInvitingUserId] = useState<string | null>(null);
  const [isConsultationModalVisible, setConsultationModalVisible] =
    useState(false);
  const userAloneCheckTimer = useRef<NodeJS.Timeout | null>(null);
  const alonePromptTimer = useRef<NodeJS.Timeout | null>(null);
  const callWSRef = useRef<null | Centrifuge>(null);
  const isUserClosingTabRef = useRef<boolean>(false);
  const [countdown, setCountdown] = useState(20);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const { token } = useToken();

  const config: CustomizationOptions = {
    videoSDKJWT: callToken,
    sessionName,
    userName,
    sessionPasscode,
    // options: {
    //   init: {
    //     enforceVirtualBackground: true,
    //   },
    //   audio: {
    //     backgroundNoiseSuppression: true,
    //   },
    //   video: {
    //     originalRatio: true,
    //   },
    // },
    // features: ['video', 'audio', 'settings', 'chat'],
    featuresOptions: {
      video: {
        enable: true,
        originalRatio: true,
        virtualBackground: true,
        enforceMultipleVideos: true
      },
      audio: {
        enable: true,
        originalSound: true,
        backgroundNoiseSuppression: true,
        syncButtonsOnHeadset: true
      },
      chat: {
        enable: true,
        enableEmoji: true
      },
      users: {
        enable: false
      },
      settings: {
        enable: true
      },
      preview: {
        enable: isPreviewEnabled
      },
      invite: {
        enable: false
      },
      footer: {
        enable: true
      },
      header: {
        enable: false
      },
      theme: {
        enable: true,
        defaultTheme: "light"
      },
      recording: {
        enable: false
      },
      share: {
        enable: false
      },
      subsession: {
        enable: false
      },
      feedback: { enable: false },
      caption: { enable: false },
      virtualBackground: {
        enable: true,
        enforceVirtualBackground: true,
        allowVirtualBackgroundUpload: true,
        virtualBackgrounds: [
          {
            url: "./td-vbg.png",
            displayName: "trudoc"
          }
        ]
      }
    }
  };

  function closeWindowForDoctorOrAgent() {
    if (role === Role.Doctor || role === Role.Agent) {
      window.close();
    }
  }

  // useEffect(() => {
  //   const handleBeforeUnload = (e: BeforeUnloadEvent) => {
  //     if (role === Role.Agent || role === Role.Doctor) {
  //       //safe check just to show only for agent/doc mobile will be not valid
  //       //this ref check is because when user clicking on leave session it was asking there also
  //       if (!isUserClosingTabRef.current) {
  //         e.preventDefault();
  //         e.returnValue =
  //           "You are about to leave the video consultation. Are you sure you want to exit?";
  //       }
  //     }
  //   };

  //   window.addEventListener("beforeunload", handleBeforeUnload);

  //   return () => {
  //     window.removeEventListener("beforeunload", handleBeforeUnload);
  //     // window.alert('<---Clean Up Called For Testing Ignore--->');
  //   };
  // }, []);

  const checkIfUserAlone = () => {
    const users = uitoolkit.getAllUser();

    if (users.length === 1) {
      if (!alonePromptTimer.current) {
        alonePromptTimer.current = setTimeout(() => {
          setConsultationModalVisible(true);
        }, checkUserAloneInCallTime);
      }
    } else {
      if (alonePromptTimer.current) {
        clearTimeout(alonePromptTimer.current);
        alonePromptTimer.current = null;
      }
    }
  };

  useEffect(() => {
    if (isConsultationModalVisible) {
      setCountdown(20);

      countdownIntervalRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownIntervalRef.current!);
            handleConsultationEnd();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [isConsultationModalVisible]);

  function joinSession() {
    const sessionContainer = sessionContainerRef.current;

    if (!callToken) {
      messageApi.error("Error: Missing videoSDKJWT in the URL.");
      return;
    }

    if (!sessionContainer) {
      messageApi.error("Error: sessionContainer is null.");
      return;
    }

    try {
      uitoolkit.joinSession(sessionContainer, config);
      uitoolkit.onSessionJoined(() => {
        setIsCallJoined(true);
        // Event-based check
        uitoolkit.on("user-removed", checkIfUserAlone);
        uitoolkit.on("user-added", checkIfUserAlone);

        // Immediate check
        checkIfUserAlone();
        // Recheck every 10 seconds
        userAloneCheckTimer.current = setInterval(() => {
          checkIfUserAlone();
        }, checkUserAloneInCallTime);

        setTimeout(() => {
          if (role === Role.Doctor || role === Role.Agent) {
            checkCallStatusOnce();
          }
        }, checkCallStatusTime);
        //Patient PATIENT_JOINED_CALL starts

        // Ends
      });
    } catch (error) {
      setIsCallJoined(false);
    }
    uitoolkit.onSessionClosed(sessionClosed);
  }

  function sessionClosed() {
    //isUserClosingTabRef prevent default alert dialog on showing here
    isUserClosingTabRef.current = true;

    if (sessionContainerRef.current) {
      uitoolkit.closeSession(sessionContainerRef.current);
    }
    setIsCallJoined(false);
    uitoolkit.off("user-removed", checkIfUserAlone);
    uitoolkit.off("user-added", checkIfUserAlone);

    if (userAloneCheckTimer.current) {
      clearInterval(userAloneCheckTimer.current);
      userAloneCheckTimer.current = null;
    }

    if (alonePromptTimer.current) {
      clearTimeout(alonePromptTimer.current);
      alonePromptTimer.current = null;
    }

    setIsCallJoined(false);
    const endCallEvent = new CustomEvent("end-call");
    window.dispatchEvent(endCallEvent);
    // uitoolkit.destroy();
    // window.location.href = "/html/callend.html";

    //closing the doc/agent tab from emr after call ends
    // closeWindowForDoctorOrAgent();
  }
  const handleConsultationEnd = () => {
    //isUserClosingTabRef prevent default alert dialog on showing here
    isUserClosingTabRef.current = true;
    setConsultationModalVisible(false);
    // window.location.href = "/html/callend.html";

    // closeWindowForDoctorOrAgent();
    setIsCallJoined(false);
    const endCallEvent = new CustomEvent("end-call");
    window.dispatchEvent(endCallEvent);
    // uitoolkit.destroy();
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      joinSession();
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const checkCallStatusOnce = async () => {
    try {
      const status = await checkCallStatus(callId, token);

      if (status === CallStatusEnum.PATIENT_CANCELED) {
        let countdown = 5;

        const key = "patient-cancel-message";

        const interval = setInterval(() => {
          countdown -= 1;
          if (countdown <= 0) {
            clearInterval(interval);
          }

          messageApi.open({
            key,
            type: "warning",
            content: (
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span>
                  The patient has canceled the consultation. Redirecting in{" "}
                  <strong>{countdown}</strong> seconds...
                </span>
                <Spin size="small" />
              </span>
            ),
            duration: 0
          });
        }, 1000);

        setTimeout(() => {
          messageApi.destroy(key);
          window.location.href = "/html/callcancel.html";
        }, countdown * 1000);
      }
    } catch (error) {
      const errMsg =
        error instanceof Error ? error.message : "Unknown error occurred";
      console.error("Failed to check call status:", errMsg);
      messageApi.error(errMsg);
    }
  };

  const onlineUsersIntervalRef = useRef<(() => void) | null>(null);
  const [messageApi, contextHolder] = message.useMessage();

  const toggleSideMenu = async () => {
    setShowSideMenu(!showSideMenu);

    if (!showSideMenu) {
      try {
        const intervalFunction = pollOnlineUsers(
          onlineUserPollingTime,
          async (res) => {
            setDoctors(res?.data || []);
          },
          token
        );
        onlineUsersIntervalRef.current = intervalFunction;
      } catch (error) {
        console.error("Error fetching doctors:", error);
      }
    } else {
      onlineUsersIntervalRef.current?.();
      onlineUsersIntervalRef.current = null;
    }
  };

  const onNewWSMessage = useCallback(
    ({ data }: PublicationContext) => {
      if (data.msg) {
        messageApi.info(data.msg);
      }
    },
    [messageApi]
  );

  const inviteTdUser = async (doctorId: string) => {
    try {
      setInvitingUserId(doctorId);
      const response = await inviteUser(
        {
          userId: doctorId,
          callId: callId
        },
        token
      ).catch((error) => {
        console.error("Error inviting user:", error);
        // Show Error to user with toast
      });
      if (response && typeof response !== "boolean" && "data" in response) {
        const { data: socketDetails } = response;
        if (!callWSRef.current) {
          callWSRef.current = callWs.initializeWebSocket(
            socketDetails.access_token
          );
          callWs.subscribeToChannel(
            callWSRef.current,
            {
              channel_name: socketDetails.channel_name,
              channel_token: socketDetails.channel_token
            },
            {
              onPublication: onNewWSMessage
            }
          );
        }
      }
      setInvitingUserId(null);
    } catch (error) {
      console.error("Invite API Error:", error);
      messageApi.error("Failed to send invite.");
    }
  };

  const groupedUsers = (doctors ?? []).reduce(
    (acc, user) => {
      if (user.id.toString() !== userId) {
        if (user.group === Role.Doctor) acc.doctors.push(user);
        else acc.agents.push(user);
      }
      return acc;
    },
    { doctors: [], agents: [] } as {
      doctors: OnlineUser[];
      agents: OnlineUser[];
    }
  );

  return (
    <div className="App">
      {contextHolder}
      <div className="session-wrapper">
        {/* <div
          style={{
            padding: "1rem",
            zIndex: 1000,
            position: "absolute",
            width: "100%",
            top: 0,
            backgroundColor: "gray"
          }}
        >
          TruDoc Video Widget Ready
          <br />
          Doctor: {"options.doctorId"}, User: {"options.userId"}
        </div> */}
        <div ref={sessionContainerRef} id="sessionContainer">
          {role !== Role.Patient && isCallJoined && isPipModeEnabled && (
            <Tooltip title="Invite A Doctor" placement="top">
              <Button
                type="primary"
                shape="round"
                icon={<MailOutlined />}
                size="large"
                onClick={toggleSideMenu}
                className="invite-button"
              >
                Invite
              </Button>
            </Tooltip>
          )}

          {isConsultationModalVisible && (
            <Modal
              title="Are you done with your consultation?"
              open={isConsultationModalVisible}
              maskClosable={false}
              closable={false}
              footer={[
                <Button
                  key="end"
                  type="primary"
                  onClick={handleConsultationEnd}
                >
                  Yes, End Consultation
                </Button>
              ]}
            >
              <p>
                It looks like you're alone in the call. The other participant
                has left.
                <br />
                Ending the consultation in <strong>{countdown}</strong> second
                {countdown !== 1 ? "s" : ""}...
              </p>
            </Modal>
          )}

          {/* Doctor List Drawer */}
          <Drawer
            title="Invite a Doctor or Agent"
            placement="right"
            closable
            onClose={toggleSideMenu}
            open={showSideMenu}
            width={380}
          >
            <>
              {/* Doctors Section */}
              {groupedUsers.doctors.length > 0 && (
                <>
                  <Typography.Title level={5} style={{ marginBottom: 12 }}>
                    Doctors
                  </Typography.Title>
                  <List
                    itemLayout="horizontal"
                    dataSource={groupedUsers.doctors}
                    renderItem={(doctor) => (
                      <List.Item
                        style={{
                          border: "1px solid #f0f0f0",
                          borderRadius: 8,
                          marginBottom: 10,
                          padding: "8px 12px"
                        }}
                        actions={[
                          <Button
                            loading={!!invitingUserId}
                            disabled={doctor.id === invitingUserId}
                            type="primary"
                            size="small"
                            onClick={() => inviteTdUser(doctor.id)}
                          >
                            Invite
                          </Button>
                        ]}
                      >
                        <List.Item.Meta
                          avatar={
                            <div
                              style={{
                                backgroundColor: "#1890ff",
                                borderRadius: "50%",
                                width: 40,
                                height: 40,
                                color: "#fff",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                fontWeight: "bold"
                              }}
                            >
                              {doctor.name?.[0]}
                            </div>
                          }
                          title={
                            <Typography.Text>{doctor.name}</Typography.Text>
                          }
                          description="Available"
                        />
                      </List.Item>
                    )}
                  />
                </>
              )}

              {/* Agents Section */}
              {groupedUsers.agents.length > 0 && (
                <>
                  <Typography.Title level={5} style={{ marginBottom: 12 }}>
                    Agents
                  </Typography.Title>
                  <List
                    itemLayout="horizontal"
                    dataSource={groupedUsers.agents}
                    renderItem={(agent) => (
                      <List.Item
                        style={{
                          border: "1px solid #f0f0f0",
                          borderRadius: 8,
                          marginBottom: 10,
                          padding: "8px 12px"
                        }}
                        actions={[
                          <Button
                            loading={!!invitingUserId}
                            disabled={agent.id === invitingUserId}
                            type="primary"
                            size="small"
                            onClick={() => inviteTdUser(agent.id)}
                          >
                            Invite
                          </Button>
                        ]}
                      >
                        <List.Item.Meta
                          avatar={
                            <div
                              style={{
                                backgroundColor: "#722ed1",
                                borderRadius: "50%",
                                width: 40,
                                height: 40,
                                color: "#fff",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                fontWeight: "bold"
                              }}
                            >
                              {agent.name?.[0]}
                            </div>
                          }
                          title={
                            <Typography.Text>{agent.name}</Typography.Text>
                          }
                          description="Available"
                        />
                      </List.Item>
                    )}
                  />
                </>
              )}

              {(!doctors ||
                doctors.length === 0 ||
                (groupedUsers.doctors.length === 0 &&
                  groupedUsers.agents.length === 0)) && (
                <Typography.Text type="secondary">
                  No doctors or agents are currently online.
                </Typography.Text>
              )}
            </>
          </Drawer>
        </div>
      </div>
    </div>
  );
};

export default VideoCall;
